import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { untranslatedString } from 'models/translated-string';
import { formatNumberMachine } from '@cf-shared/shared-util';
import { AuthService } from 'services/auth.service';
import { UserDb } from 'services/user-db.service';
import { legacyEquipmentId } from 'services/exercise-stat-equipment';
import { ExerciseDbService, Exercise, CustomExercise, isCustomExerciseById } from 'services/exercise-db.service';
import { isNameBasedExerciseId, generateCustomExerciseId } from '@cf-shared/data-model/exercise';
import { DebugService } from 'services/debug.service';
import { DateTime } from 'luxon';
import { SettingsService } from 'services/settings.service';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { CoachExerciseSyncService } from 'services/coach-exercise-sync.service';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { CsPage, NavController } from 'providers/nav-controller';
import { LoggingService } from 'services/logging.service';
import { AdvancedMethodsRMTesting } from '@cf-shared/enums';
import { PopupMenuOptions, PopupMenuProvider, PopupMenuButton } from 'providers/popup-menu/popup-menu';
import { formatExerciseSets, formatOneExerciseSet, getMeasureUnitConversion, formatRelativeDay, describeExerciseState } from 'util/formatting';
import { TranslatedString } from 'models/translated-string';
import { exportFileAsync, trySystemShare } from 'services/system-share';
import { CsTabComponent } from 'components/cs-tab/cs-tab';
import { CsMarkdownComponent } from 'components/cs-markdown/cs-markdown';
import { DateRangeChangeEvent, TimePeriod } from 'components/cs-date-range-toolbar/cs-date-range-toolbar';
import { combinedEntryForExercise, Log_Workout } from '@cf-shared/data-model/log-workout';
import { LogRecordState } from '@cf-shared/data-model/user-log';
import { FitnessState, ExerciseStat, ExerciseStatStateDescription, ExerciseStatState, MyoRepsMode, DeclaredState } from '@cf-shared/data-model/user-state';
import {
  clampRestSeconds,
  formatRestDuration,
  getRestTimerOptions,
  REST_TIMER_DEFAULT_SECONDS,
  REST_TIMER_MAX_SECONDS,
  REST_TIMER_MIN_SECONDS,
  REST_TIMER_STEP_SECONDS,
} from '@cf-shared/data-model/rest-timer';
import { upperFirst } from 'lodash-es';
import { IEquipmentSearchPage } from 'pages/equipment/equipment-search-interface';
import { ProgressChartSeries, ProgressChartDataPoint, ProgressChartAxisConfig } from 'components/cs-progress-chart/cs-progress-chart';
import { hasClientSmallScreen, hasTouchSupport } from 'environments/environment-helpers';
import { getSharedUrlOrigin } from 'util/backend-origin';
import { PopoverController } from 'providers/popover-controller';
import { VirtualKeyboardService } from 'services/virtual-keyboard.service';
import { InputValue } from 'components/virtual-keyboard/virtual-keyboard';
import { CsLogDetailPopover, CsLogDetailPopoverParams } from 'components/cs-log-detail-popover/cs-log-detail-popover';
import { ChartDataExerciseProgressService } from 'services/chart-data-exercise-progress.service';
import { PlanMerge, WorkoutLoaderService } from 'services/workout-loader.service';
import { ExerciseStatsPageParams } from './exercise-stats-interface';
import { ExerciseDiffResult } from 'pages/exercise-diff/exercise-diff-page-interface';
import { resolveLocalCustomExerciseAsync } from 'services/exercise-fork-update';
import { CoachForkUpdateFlow } from 'services/exercise-fork-update-flow';
import { pagesToUrl } from 'utils/url-parser';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { LoadingController } from 'providers/loading-controller';
import { deepClone } from '@cf-shared/deep-helpers';
import * as crypto from 'crypto';
import { resolveAnyExerciseByIdAsync } from 'services/exercise-resolve';

class SetInfo {
  setText: string;
  dataText: string;
  userNotes?: string;
}
class WorkoutInfo {
  expanded?: boolean;
  date: DateTime;  // for filtering by date range
  dateText: string;
  dataText: string;
  hasNotes: boolean;
  notesPreview: string;  // joined notes from all sets
  sets: SetInfo[];
}

@Component({
  selector: 'page-exercise-stats',
  templateUrl: 'exercise-stats.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ExerciseStatsPage extends CsPage<ExerciseStatsPageParams> {
  protected LL = LL;
  protected exerciseId: string;
  protected exerciseName: string;
  protected exercise: Exercise;
  protected isCustom = false;
  protected isShared = false;
  protected isImported = false;
  protected hasOriginalUpdate = false;
  protected helpId: string;
  protected subsection: 'instructions' | 'log' | 'settings' = 'log';
  private readonly tabOrder: ('instructions' | 'log' | 'settings')[] = ['instructions', 'log', 'settings'];
  protected loading = false;
  // set when loadAsync caught an error, so the Log tab can show an honest "couldn't
  // load — try again" state instead of a misleading "No Data in Range" empty view.
  protected loadFailed = false;
  public noData = true;
  protected workouts: WorkoutInfo[] = [];
  protected fitnessState: FitnessState;
  protected declaredState: DeclaredState;
  protected fitnessPlan: FitnessPlan;
  protected exerciseStat: ExerciseStat;
  public stateStateDesc = {} as ExerciseStatStateDescription<TranslatedString>;
  state: ExerciseStatState;
  private allWorkouts: Log_Workout[] = [];

  public MyoRepsMode = MyoRepsMode;
  public AdvancedMethodsRMTesting = AdvancedMethodsRMTesting;

  protected timePeriod: TimePeriod = 'month';
  protected startDate: string = '';
  protected endDate: string = '';

  // Chart data for cs-progress-chart
  protected chartSeries: ProgressChartSeries[] = [];
  protected chartData: ProgressChartDataPoint[] = [];
  protected chartAxisConfig: ProgressChartAxisConfig = { stacked: true };

  private docWatcher: DocWatcher;
  private coachAppliedSub: Subscription | null = null;
  // The check-for-update / diff-picker / apply sequence, shared with exercise-switch (the
  // other screen that applies at the canonical-doc level). Constructed below.
  private forkUpdateFlow: CoachForkUpdateFlow;

  // Resolves a fork's source from the user's OWN DB so a same-user local source isn't
  // reported as a deleted shared exercise (#3808). Passed into checkImportedExerciseForUpdateAsync.
  private resolveLocalSource = (id: string) => resolveLocalCustomExerciseAsync(this.userDb, id);

  @ViewChild('segment', { static: true }) segment: CsTabComponent;
  @ViewChild('instructionsMarkdown') instructionsMarkdown?: CsMarkdownComponent;

  constructor(
    public navCtrl: NavController,
    protected auth: AuthService,
    private userDb: UserDb,
    private exerciseDb: ExerciseDbService,
    private debug: DebugService,
    protected settings: SettingsService,
    protected alertCtrl: AlertController,
    private logging: LoggingService,
    private popupCtrl: PopupMenuProvider,
    private cloudFunctionsService: CloudFunctionsService,
    private toastController: ToastController,
    private loadingCtrl: LoadingController,
    private cdr: ChangeDetectorRef,
    protected exerciseProgressService: ChartDataExerciseProgressService,
    private popoverCtrl: PopoverController,
    private workoutLoader: WorkoutLoaderService,
    private keyboardService: VirtualKeyboardService,
    private coachExerciseSync: CoachExerciseSyncService,
  ) {
    super();
    this.forkUpdateFlow = new CoachForkUpdateFlow({
      pageName: 'exercise-stats',
      navCtrl: this.navCtrl,
      userDb: this.userDb,
      exerciseDb: this.exerciseDb,
      toastController: this.toastController,
      logging: this.logging,
      settings: this.settings,
      getDocWatcher: () => this.docWatcher,
      isViewDestroyed: () => this.viewDestroyed,
      // stats has no loaded exercise list, so this is best-effort (raw id; only used in the
      // rare case the coach changed `similarExerciseId`).
      resolveSimilarExerciseName: id => id || LL.editExercise.alerts.updateAvailable.empty(),
    });
  }

  // touch devices get the project's custom roller keyboard; non-touch
  // keeps the cs-select dropdown (matches cs-input-time's approach)
  isTouchDevice(): boolean {
    return hasTouchSupport();
  }

  // open the virtual keyboard in single-roller mode for the per-exercise
  // override; picks commit through the existing ExerciseStat save path
  handleRestOverrideRollerOpen(event: Event): void {
    if (!this.isTouchDevice()) return;
    const target = event.currentTarget as HTMLElement;
    const values = this.restTimerOptions.map(o => InputValue.fromLabel(o.seconds, o.label));
    void this.keyboardService.startKeyboard(target, () => ({
      startInRollerMode: true,
      // a rest duration reads as M:SS and the keypad has no colon key, so a
      // typed number would be ambiguous; offer the roller only (#4904)
      rollerOnly: true,
      preselectValuesNumeric1: values,
      suggestedValueNumeric1: this.restOverrideSeconds,
      currentValueNumeric1: this.restOverrideSeconds,
      rollerLabel1: LL.workouts.restTimer.rollerLabel(),
      onValueSelectedNumeric: (v: { value1: number; value2?: number }) => {
        this.exerciseStat.restSeconds = clampRestSeconds(Number(v.value1));
        this.handleExerciseStatsChanged();
      },
    }));
  }

  async ionViewCanEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cdr.markForCheck();
      });
    }
    if (!this.coachAppliedSub) {
      // live-refresh the "Update Available" banner: a coach edit lands via
      // coach-exercise-sync's apply pipeline (a same-device local write the page
      // docWatcher doesn't surface as an update signal), so re-detect on each apply.
      // debounce because one coach edit can apply several docs in quick succession.
      this.coachAppliedSub = this.coachExerciseSync.coachExerciseApplied$
        .pipe(debounceTime(500))
        .subscribe(() => {
          if (this.viewDestroyed) return;
          void this.detectOriginalUpdateAsync();
        });
    }

    try {
      this.exerciseId = this.navParams.data.id;
      // if no exercise ID provided (e.g. future day with unresolved exercises), Sentry 3T9
      if (!this.exerciseId) {
        throw new Error('No exercise ID provided');
      }
      this.exercise = await resolveAnyExerciseByIdAsync(this.exerciseId, this.exerciseDb, this.userDb, this.docWatcher);
      if (!this.exercise) {
        throw new Error(`Can't find exercise "${this.exerciseId}".`);
      }
      this.exerciseName = this.exercise.name;
      this.isCustom = isCustomExerciseById(this.exerciseId);
      if (this.isCustom) {
        const customExercise = this.exercise as CustomExercise;
        this.isShared = !!customExercise.local?.shared;
        this.isImported = !!customExercise.local?.sourceExerciseId;
        if (this.isImported) {
          // fire-and-forget: surface the "Update Available" banner as a discovery hint.
          void this.detectOriginalUpdateAsync();
        }
      }
    } catch (err) {
      return this.auth.failPageGuardAsync(`Unknown workout id: '${this.exerciseId}'`, { cause: err });
    }

    this.helpId = '@instructions:' + this.exerciseId;

    [this.fitnessPlan, this.fitnessState, this.declaredState] = await Promise.all([
      this.userDb.loadPlanAsync(this.docWatcher),
      this.userDb.loadFitnessStateAsync(this.docWatcher),
      this.userDb.loadDeclaredStateAsync(this.docWatcher),
    ]);
    if (!this.fitnessState.exerciseStats[this.exerciseId]) {
      this.fitnessState.exerciseStats[this.exerciseId] = {} as ExerciseStat;
    }
    this.exerciseStat = this.fitnessState.exerciseStats[this.exerciseId];
    if (this.exerciseStat.preferMyoReps === undefined) {
      this.exerciseStat.preferMyoReps = MyoRepsMode.Auto;
    }
    if (this.exerciseStat.preferRMTesting === undefined) {
      this.exerciseStat.preferRMTesting = AdvancedMethodsRMTesting.Automatic;
    }

    // the options section is only shown if the myo reps feature is enabled
  }

  async ionViewWillEnter() {
    if (this.navParams.data.mode) {
      this.subsection = this.navParams.data.mode;
    }

    await this.loadAsync();
  }
  async loadAsync() {
    this.loading = true;
    this.loadFailed = false;
    this.cdr.markForCheck();
    try {
      let now_local = this.debug.getNowLocal();
      let now_utc = now_local.toUTC();
      let locale = this.settings.locale;

      let { state } = ExerciseStat.getExerciseState(this.exerciseId, now_utc, this.fitnessPlan, this.fitnessState);
      this.state = state;
      this.stateStateDesc = describeExerciseState(this.state);

      this.allWorkouts = await this.userDb.getWorkoutsForExerciseAsync(this.exerciseId);

      this.workouts = this.allWorkouts.flatMap((wo: Log_Workout): WorkoutInfo[] => {
        // the workoutByExerciseId view returns this workout because its SAVED log includes this
        // exerciseId, so read the logged exercise directly rather than re-projecting the old log
        // through the *current* plan. getEffectiveExercises re-stubs/discards an unstarted slot
        // after a re-plan or exercise swap, dropping the id and crashing here (#3469).
        //
        // one workout can hold this exercise in more than one entry — an exercise changed after
        // sets were logged keeps them in an entry of its own, and changing back adds another
        // (#5507) — so the row shows the sets of all of them, in the order they were done
        let exercise = combinedEntryForExercise(wo.exercises, this.exerciseId);
        if (!exercise) {
          // the view emitted this id from this doc, so a miss means the doc changed under us
          // (deleted/edited between index build and read). skip the row instead of crashing,
          // and surface it so a real invariant break stays visible.
          this.logging.trackSilentProblem('exercise-stats: logged exercise missing for view-matched workout', {
            workoutId: wo._id, exerciseId: this.exerciseId,
          });
          return [];
        }
        let workoutDate = DateTime.fromISO(wo.time_utc).toLocal();
        let sets = exercise.sets.map((s, i) => ({
          setText: LL.exerciseStats.setNumber({ number: i + 1 }),
          dataText: s.state === LogRecordState.Skipped
            ? LL.workouts.detail.exerciseHeader.skipped() as string
            : formatOneExerciseSet(s, locale),
          userNotes: s && s.userNotes,
        }));
        return [{
          date: workoutDate,
          dateText: upperFirst(formatRelativeDay(now_local, workoutDate)),
          dataText: formatExerciseSets(exercise, locale),
          hasNotes: sets.some(s => !!s.userNotes),
          notesPreview: sets.filter(s => s.userNotes).map(s => s.userNotes).join(' • '),
          sets,
        }];
      });
      this.workouts.reverse();

      // "no history" reflects whether any renderable rows survived the per-row read (a row is
      // skipped only when its logged exercise vanished — see the flatMap guard), not just whether
      // the view returned docs. computed here, after the filter, so an all-skipped corner shows
      // the no-history empty state rather than a misleading "No Data in Range".
      this.noData = this.workouts.length === 0;

      // if on log tab, initialize chart
      if (this.subsection === 'log') {
        await this.fetchChartDataAsync();
      }
    } catch (err) {
      err.exerciseId = this.exerciseId;
      this.loadFailed = true;
      this.logging.trackAbnormalException(err, untranslatedString('Error while loading exercise data.'));
    }

    this.loading = false;
    this.cdr.markForCheck();
  }

  private async fetchChartDataAsync() {
    // initialize series config from service
    this.chartSeries = this.exerciseProgressService.getSeries(
      this.showWeight,
      this.show1RM,
      this.showTonnage,
      this.exerciseStat?.weight_unit
    );
    this.chartAxisConfig = this.exerciseProgressService.getAxisConfig();

    // fetch data from service
    this.chartData = await this.exerciseProgressService.fetchDataAsync(
      this.exerciseId,
      this.docWatcher,
      this.startDate || undefined,
      this.endDate || undefined,
      { includeRMTests: this.includeRMtests }
    );

    this.cdr.markForCheck();
  }

  private updateChartSeriesVisibility() {
    // update hidden state on existing series
    this.chartSeries = this.exerciseProgressService.getSeries(
      this.showWeight,
      this.show1RM,
      this.showTonnage,
      this.exerciseStat?.weight_unit
    );
    this.cdr.markForCheck();
  }

  handleSegmentChange() {
    // if switching to log tab, fetch chart data
    if (this.subsection === 'log') {
      void this.fetchChartDataAsync();
    }
    this.cdr.markForCheck();
  }

  handleSwipeForward() {
    const i = this.tabOrder.indexOf(this.subsection);
    if (i < this.tabOrder.length - 1) {
      this.subsection = this.tabOrder[i + 1];
      this.handleSegmentChange();
    }
  }

  handleSwipeBack() {
    const i = this.tabOrder.indexOf(this.subsection);
    if (i > 0) {
      this.subsection = this.tabOrder[i - 1];
      this.handleSegmentChange();
    }
  }

  handleDateRangeChange(event: DateRangeChangeEvent) {
    this.timePeriod = event.timePeriod;
    this.startDate = event.startDate.toISODate() ?? '';
    this.endDate = event.endDate.toISODate() ?? '';
    void this.fetchChartDataAsync();
    this.cdr.markForCheck();
  }

  handleExerciseStatsChanged() {
    void this.saveStatsAsync();
    this.cdr.markForCheck();
  }

  // rest-timer per-exercise override getters/handlers: surface a 15s-step
  // current displayed value: either the override, or the global default
  // used as a starting point when the user enables the override.
  // publicly readable (template binds cs-select's value to this).
  get restOverrideSeconds(): number {
    const v = this.exerciseStat?.restSeconds;
    if (typeof v === 'number' && v > 0) return clampRestSeconds(v);
    return clampRestSeconds(this.declaredState?.defaultRestSeconds ?? REST_TIMER_DEFAULT_SECONDS);
  }

  get restOverrideLabel(): string {
    return formatRestDuration(this.restOverrideSeconds);
  }

  // discrete duration options driving the dropdown (shared with program-settings)
  readonly restTimerOptions = getRestTimerOptions();

  canDecRestOverride(): boolean {
    return this.restOverrideSeconds > REST_TIMER_MIN_SECONDS;
  }

  canIncRestOverride(): boolean {
    return this.restOverrideSeconds < REST_TIMER_MAX_SECONDS;
  }

  // step the override by +/- 1 (in step-units) and persist
  handleRestOverrideStep(direction: 1 | -1): void {
    const next = clampRestSeconds(this.restOverrideSeconds + direction * REST_TIMER_STEP_SECONDS);
    this.exerciseStat.restSeconds = next;
    this.handleExerciseStatsChanged();
  }

  // pick a value directly from the dropdown / roller
  handleRestOverrideSelect(value: number): void {
    this.exerciseStat.restSeconds = clampRestSeconds(Number(value));
    this.handleExerciseStatsChanged();
  }

  // local flag that tracks whether the user has opened the override card.
  // we don't persist anything on toggle-on: the UI shows the global default
  // as a placeholder, but we only write to ExerciseStat.restSeconds when
  // the user actually picks a non-default value. that way toggling "to
  // peek" doesn't leave behind a frozen same-value override that would
  // fail to track future changes to the global default.
  protected overrideOpened = false;

  get hasRestOverride(): boolean {
    if (this.overrideOpened) return true;
    const v = this.exerciseStat?.restSeconds;
    return typeof v === 'number' && v > 0;
  }

  // toggle override on/off from the card header. on = open the picker (no
  // persistence yet); off = clear the stored override and close the picker.
  handleRestOverrideToggle(enabled: boolean): void {
    if (enabled) {
      this.overrideOpened = true;
    } else {
      this.overrideOpened = false;
      if (this.exerciseStat?.restSeconds !== undefined) {
        this.exerciseStat.restSeconds = undefined;
        this.handleExerciseStatsChanged();
      }
    }
  }

  async saveStatsAsync() {
    await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
  }

  handleAllow() {
    void (async () => {
      let confirmed = await this.alertCtrl.showAlertAsync<boolean>({
        title: LL.exerciseStats.alerts.stopAvoidingTitle(),
        message: LL.exerciseStats.alerts.stopAvoidingMessage(),
        buttons: [
          { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true },
          { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        ],
      });

      if (confirmed) {
        ExerciseStat.unmarkSwitched(this.exerciseStat);
        await this.saveStatsAsync();
        await this.loadAsync();
        this.cdr.markForCheck();
      }
    })();
  }

  handleEquipment() {
    void this.navCtrl.pushAsync(IEquipmentSearchPage, {
      exercise: this.exerciseId,
    });
  }

  showWeight: boolean = true;
  show1RM: boolean = false;
  showTonnage: boolean = false;
  includeRMtests: boolean = false;

  get activeFilterCount(): number {
    let count = 0;
    if (this.showWeight) count++;
    if (this.show1RM) count++;
    if (this.showTonnage) count++;
    if (this.includeRMtests) count++;
    return count;
  }

  // checks if chartData has any actual values (not just empty date entries)
  get hasChartDataInRange(): boolean {
    return this.chartData.some(d => d.weight !== undefined || d['1rm'] !== undefined || d.tonnage !== undefined);
  }

  // filters workouts by the selected date range
  get workoutsInRange(): WorkoutInfo[] {
    // if no date range selected, return all workouts
    if (!this.startDate || !this.endDate) {
      return this.workouts;
    }
    let start = DateTime.fromISO(this.startDate).startOf('day');
    let end = DateTime.fromISO(this.endDate).endOf('day');
    return this.workouts.filter(w => w.date >= start && w.date <= end);
  }

  handleMeasureFiltersClicked(ev: Event) {
    let opts: PopupMenuOptions = { buttons: [], selectMultiple: true, targetFromElement: ev.target as HTMLElement };

    opts.buttons.push({
      text: LL.exerciseStats.filters.weight(),
      selectState: this.showWeight,
      handler: () => {
        this.showWeight = !this.showWeight;
        this.updateChartSeriesVisibility();
      },
    });
    opts.buttons.push({
      text: LL.exerciseStats.filters.oneRM(),
      selectState: this.show1RM,
      handler: () => {
        this.show1RM = !this.show1RM;
        this.updateChartSeriesVisibility();
      },
    });
    opts.buttons.push({
      text: LL.exerciseStats.filters.tonnage(),
      selectState: this.showTonnage,
      handler: () => {
        this.showTonnage = !this.showTonnage;
        this.updateChartSeriesVisibility();
      },
    });
    opts.buttons.push({
      text: LL.exerciseStats.filters.includeRMTests(),
      selectState: this.includeRMtests,
      handler: () => {
        this.includeRMtests = !this.includeRMtests;
        // includeRMTests affects the data, so refetch
        void this.fetchChartDataAsync();
      },
    });
    void this.popupCtrl.createMenu(opts);
  }

  async handleSettingsClick(event: Event) {
    const targetElement = event.currentTarget as HTMLElement;

    const buttons: PopupMenuButton[] = [];
    // For an imported fork, offer the same in-place "Update" / "Check for Update" re-pull as
    // the exercise-switch ⋮ — label "Update" when a genuine new rev is available, else the
    // always-available "Check for Update".
    if (this.canCheckForUpdate()) {
      buttons.push({
        text: this.hasOriginalUpdate
          ? LL.exerciseSwitch.buttons.update()
          : LL.exerciseSwitch.buttons.checkForUpdate(),
        value: 'checkForUpdate',
        icon: 'refresh',
        selectState: false,
        handler: () => void this.handleCheckForUpdate(),
      });
    }
    buttons.push({
      text: LL.exerciseStats.exportAsCsv(),
      value: 'exportCsv',
      icon: 'download',
      selectState: false,
      handler: () => void this.exportAsCsv()
    });

    await this.popupCtrl.createMenu({
      subsections: [{ header: untranslatedString(''), buttons }],
      selectMultiple: false,
      targetFromElement: targetElement,
      horizontalAlign: 'right',
    });
  }

  private async exportAsCsv() {
    const locale = this.settings.locale;
    const weightUnit = this.exerciseStat?.weight_unit;
    const { factor: weightFactor, unitName } = getMeasureUnitConversion('addedweight_kg', locale, weightUnit);

    const csvRows: string[][] = [];
    csvRows.push([
      'Date',
      `Weight [${unitName}]`,
      'Reps',
      `Est. 1RM [${unitName}]`,
      `Tonnage [${unitName}]`,
      'Notes',
    ]);

    // iterate all workouts (full history, not filtered by date range)
    for (const wo of this.allWorkouts) {
      const exercises = this.workoutLoader.getEffectiveExercises(wo, this.fitnessPlan, PlanMerge.AsStored);
      // every entry of this exercise in the workout, not just the first (#5507)
      const exercise = combinedEntryForExercise(exercises, this.exerciseId);
      if (!exercise) continue;

      const date = DateTime.fromISO(wo.time_utc).toLocal().toISODate();

      for (const set of exercise.sets) {
        // skip null/skipped sets
        if (!set || set.state === LogRecordState.Skipped) continue;
        if (!set.stats || set.stats.weight_kg == null || set.stats.reps == null) continue;

        // CSV export → machine formatting (plain '.', never grouped, so it stays parseable). #4204
        const weight = formatNumberMachine(set.stats.weight_kg * weightFactor, { decimals: 1 });
        const reps = String(set.stats.reps);
        const est1rm = set.stats.estimated1rm_kg != null ? formatNumberMachine(set.stats.estimated1rm_kg * weightFactor, { decimals: 1 }) : '';
        const tonnage = formatNumberMachine(set.stats.weight_kg * set.stats.reps * weightFactor, { decimals: 1 });
        const notes = set.userNotes || '';

        csvRows.push([date, weight, reps, est1rm, tonnage, notes]);
      }
    }

    // convert to CSV string, quoting fields that contain commas or quotes
    const csvString = csvRows.map(row =>
      row.map(cell => {
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    ).join('\n');

    const filename = this.exerciseName.replace(/[^a-zA-Z0-9_\- ]/g, '').trim() + '.csv';
    // hand the file to the user (browser download, or the share sheet in the native apps)
    try {
      await exportFileAsync({ data: csvString, filename, mimeType: 'text/csv;charset=utf-8;' });
    } catch (err) {
      this.logging.trackAbnormalException(err, LL.errors.userFacing.exportFailed());
    }
  }

  // returns true if on small screen (mobile/tablet)
  get isSmallScreen(): boolean {
    return hasClientSmallScreen();
  }

  // returns the name of the currently selected equipment
  get equipmentName(): string {
    let equipmentId = legacyEquipmentId(this.exerciseStat);
    if (equipmentId && this.fitnessState?.allEquipment?.[equipmentId]) {
      return this.fitnessState.allEquipment[equipmentId].name;
    }
    return LL.exerciseStats.settings.notSelectedYet();
  }

  protected handleShareIconClick(event: Event) {
    if (this.isShared) {
      // already shared — show options menu
      lockClick(event, this.showShareMenu(event));
    } else if (this.isImported) {
      // imported exercise — share original link
      const sourceId = (this.exercise as CustomExercise).local?.sourceExerciseId;
      if (sourceId) {
        lockClick(event, this.shareExerciseLink(sourceId));
      }
    } else {
      lockClick(event, this.handleShareExercise());
    }
  }

  private async showShareMenu(event: Event) {
    const buttons: PopupMenuButton[] = [
      {
        text: LL.exerciseStats.buttons.shareLink(),
        handler: () => void this.shareExerciseLink(this.exerciseId),
      },
      {
        text: LL.exerciseStats.buttons.stopSharing(),
        handler: () => void this.handleUnshareExercise(),
      },
    ];
    await this.popupCtrl.createMenu({
      buttons,
      targetFromElement: event.target as HTMLElement,
    });
  }

  private async handleShareExercise() {
    const loader = this.loadingCtrl.create();
    void loader.present();
    try {
      let customExercise = this.exercise as CustomExercise;

      // if name-based ID, re-generate as SHA-based
      if (isNameBasedExerciseId(customExercise._id)) {
        const oldExercise = customExercise;
        const newExercise = deepClone(customExercise) as CustomExercise;
        delete newExercise._rev;
        generateCustomExerciseId(this.auth.user.uid, newExercise, crypto);
        await this.userDb.reIdCustomExerciseAsync(oldExercise, newExercise);
        customExercise = newExercise;
        this.exercise = newExercise;
        this.exerciseId = newExercise._id;
      }

      // mark as shared
      customExercise.local = { ...customExercise.local, shared: true };
      await this.userDb.saveCustomExerciseAsync(customExercise, DatabaseWrapper.getTransactionId());

      // publish to global_exercises
      await this.cloudFunctionsService.publishExerciseAsync({ exercise: customExercise, adminMode: false });

      this.isShared = true;
      this.cdr.markForCheck();

      // share link
      await this.shareExerciseLink(this.exerciseId);
    } catch (err) {
      this.logging.trackAbnormalException(err, untranslatedString('Error sharing exercise'));
    } finally {
      void loader.dismiss();
    }
  }

  private async handleUnshareExercise() {
    const customExercise = this.exercise as CustomExercise;
    try {
      await this.cloudFunctionsService.unpublishExerciseAsync({ exercise: customExercise, adminMode: false });
      customExercise.local = { ...customExercise.local, shared: false };
      await this.userDb.saveCustomExerciseAsync(customExercise, DatabaseWrapper.getTransactionId());
      this.isShared = false;
      this.cdr.markForCheck();
    } catch (err) {
      this.logging.trackAbnormalException(err, untranslatedString('Error unsharing exercise'));
    }
  }

  /**
   * The `exercise` segment belongs to `ViewExercisePage`, the read-only screen a recipient
   * should land on. It is deliberately the SAME address this built before that page existed,
   * when the read-only screen was a mode of the edit page — links already sent to other
   * people cannot be recalled, so the address has to keep meaning what it always meant.
   * The `mode=view` those older links carry is accepted and ignored, and is not built here
   * any more because the page has no modes.
   */
  private buildShareUrl(exerciseId: string): string {
    const path = pagesToUrl([{ segment: 'exercise', params: { id: exerciseId } }]);
    return `${getSharedUrlOrigin()}${path}`;
  }

  private async shareExerciseLink(exerciseId: string) {
    const url = this.buildShareUrl(exerciseId);
    await this.showShareSheet(url, this.exerciseName);
  }

  // show the system share sheet for an exercise URL, fall back to clipboard if share is unavailable
  private async showShareSheet(url: string, title: string) {
    const result = await trySystemShare({ title, url });
    if (result !== 'unavailable') return;
    // if share not supported, fall back to clipboard
    await navigator.clipboard.writeText(url);
    this.toastController.showToast({
      message: LL.exerciseStats.toasts.linkCopied(),
      type: ToastType.SuccessLight,
      duration: 2000,
    });
  }

  // Settings ⋮ / "Update Available" banner gate: this exercise is an imported fork
  // (carries `local.sourceExerciseId`) that is NOT a coach copy. Mirrors the exercise-switch
  // ⋮ population so a fork can be re-checked from stats too.
  protected canCheckForUpdate(): boolean {
    const local = (this.exercise as CustomExercise | undefined)?.local;
    return this.isCustom && !!local?.sourceExerciseId && !local?.coachShared;
  }

  // In-place "Check for Update" / "Update": re-checks the source, then presents the per-field
  // picker as an app-level modal and applies at the canonical-doc level (no navigation to the
  // edit page). Reached from the "Update Available" banner action and the settings ⋮ item.
  // The whole sequence lives in `CoachForkUpdateFlow`, shared with exercise-switch; what is
  // page-specific is the refresh below.
  protected async handleCheckForUpdate() {
    await this.forkUpdateFlow.checkForUpdateAsync({
      fork: this.exercise as CustomExercise,
      onApplied: canonical => {
        // the saved canonical is the identity-map object; point the page at it and clear the
        // "Update Available" banner (the rev is now seen / applied). refresh the cached
        // exerciseName too (the header binds it, not exercise.name) so a name change shows
        // immediately instead of staying stale until the page reloads.
        this.exercise = canonical;
        this.exerciseName = canonical.name;
        this.hasOriginalUpdate = false;
        this.cdr.markForCheck();
        // the Instructions tab renders the description / video through cs-markdown, which
        // resolves its content once on init. A local apply is NOT a sync pull, so it does not
        // fire cs-markdown's DocWatcher — re-render it explicitly so the new description shows
        // without a page reload. (`?.` is defensive; cs-tab-content renders eagerly, so the
        // ViewChild is reliably set once the view inits.)
        void this.instructionsMarkdown?.initializeAsync();
      },
    });
  }

  // returnCallback from the ExerciseDiffPage modal. This name has to stay a method on the
  // page: `NavController` finds the handler by walking the nav stack for a page instance
  // carrying it. The body is shared — see `CoachForkUpdateFlow.handlePickerResultAsync`.
  async onDiffPickerResult(_context: unknown, result?: ExerciseDiffResult): Promise<void> {
    await this.forkUpdateFlow.handlePickerResultAsync(result);
  }

  // Detect whether the imported fork's upstream source has a genuinely-new revision and
  // drive the "Update Available" banner. Same gate as edit-exercise-page's rev-check
  // (`!!sourceExerciseId`): self-reference imports (sourceExerciseId === _id) are still
  // legitimate rev-check candidates because the source lives in global_exercises while the
  // local lives in userdb-* — different docs in different DBs. originalDeleted is
  // intentionally NOT consumed here — the "Original No Longer Available" warning lives on
  // edit-exercise-page only, keeping stats actionable-only (banner shows only when there's
  // something to do). Re-runnable: called on load AND on each live coachExerciseApplied$.
  private async detectOriginalUpdateAsync(): Promise<void> {
    if (!this.isImported || !isCustomExerciseById(this.exerciseId)) return;
    const customExercise = this.exercise as CustomExercise;
    if (!customExercise.local?.sourceExerciseId) return;
    try {
      const result = await this.exerciseDb.checkImportedExerciseForUpdateAsync(customExercise, this.docWatcher, this.resolveLocalSource);
      if (this.viewDestroyed) return;
      // determine the next banner state. a deleted source clears it (nothing to update to);
      // a resolved source assigns hasUpdate (so a live re-detect also CLEARS a stale banner
      // once the update was applied elsewhere); a swallowed network blip (`original: null`,
      // not deleted) leaves the banner as-is rather than flickering it off.
      let next = this.hasOriginalUpdate;
      if (result.deleted) next = false;
      else if (result.original) next = result.hasUpdate;
      if (this.hasOriginalUpdate !== next) {
        this.hasOriginalUpdate = next;
        this.cdr.markForCheck();
      }
    } catch (err: any) {
      // non-fatal: a failed check just leaves the banner state unchanged. Surface so a
      // systemic failure (e.g. staging DB closed, unexpected response shape) isn't invisible.
      console.error('exercise-stats: update-available check failed', err);
      this.logging.trackSilentProblem('exercise-stats: update-available check failed', {
        docId: customExercise._id, err: String(err?.message ?? err),
      });
    }
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
    this.coachAppliedSub?.unsubscribe();
    this.coachAppliedSub = null;
  }

  // trackBy function for ngFor to properly track workout items
  trackByWorkoutDate(_index: number, wo: WorkoutInfo): string {
    return wo.date.toISO();
  }

  // handles clicking on a log row
  handleLogRowClick(wo: WorkoutInfo, event: PointerEvent) {
    if (this.isSmallScreen) {
      // on mobile, open bottom sheet
      this.openLogDetailBottomSheet(wo, event.target as HTMLElement);
    } else {
      // on desktop, toggle expand/collapse
      wo.expanded = !wo.expanded;
      this.cdr.markForCheck();
    }
  }

  // opens the bottom sheet with log details
  private openLogDetailBottomSheet(wo: WorkoutInfo, targetElement: HTMLElement) {
    const dateTitle = upperFirst(formatRelativeDay(this.debug.getNowLocal(), wo.date));

    const params: CsLogDetailPopoverParams = {
      dateTitle,
      sets: wo.sets,
    };

    void CsLogDetailPopover.present(this.popoverCtrl, targetElement, params);
  }
}
